import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { restartableTask } from 'ember-concurrency';
import {
  handleFormErrorOrModal,
  handleErrorResponseWithModal
} from 'sh-www/utils/handles-errors';
import { TYPE, GLIMMER_LEGAL_TYPES } from 'sh-www/models/legal';
import { showModal } from 'sh-www/utils/modal-controls';
import ConfirmationModal from 'sh-www/components/modals/confirmation';
import clsx from 'clsx';

const PAGE_SIZE = 10;
const QUERY_PARAMS = ['type'];

const optionsModel = (value, name) => ({ name, value });

const TYPE_OPTIONS = [
  optionsModel(TYPE.CREATIVE, GLIMMER_LEGAL_TYPES[TYPE.CREATIVE]),
];

const HTML_PREVIEW_FORM_INPUTS = [
  'title',
  'content',
];

const CLASS_NAME = 'admin-legal';
const LIST_CLASS_NAME = `${CLASS_NAME}__list`;
const LIST_NO_BUTTON_CLASS_NAME = `${LIST_CLASS_NAME}--no-create-button`;

class AdminLegalController extends Controller {
  @service('state-managers.application') applicationStateManager;
  @service('commands.legal-commands') legalCommandService;
  @service('error-tracker') errorTracker;
  @service api;
  @service moment;
  @service session;
  @service shData;
  @service store;

  queryParams = QUERY_PARAMS;

  type = TYPE.CREATIVE;
  isShowingEditor = false;

  legals = [];
  page = 1;
  errors = {};

  get typeOptions() {
    return TYPE_OPTIONS;
  }

  get baseClassName() {
    return CLASS_NAME;
  }

  get listClasses() {
    return clsx(
      LIST_CLASS_NAME,
      !this.session.currentUser.superAdmin && LIST_NO_BUTTON_CLASS_NAME
    );
  }

  @computed('type')
  get toggleEditorButtonText() {
    return `Create ${GLIMMER_LEGAL_TYPES[this.type]}`;
  }

  @computed('loadLegals.last.value.meta.pagination.total')
  get total() {
    return this.loadLegals.last?.value?.meta?.pagination.total ?? 0;
  }

  @computed('loadLegals.last.value.meta.pagination.total_pages')
  get totalPages() {
    return this.loadLegals.last?.value?.meta?.pagination.total_pages ?? 0;
  }

  @computed('page')
  get disablePreviousPage() {
    return this.page <= 1;
  }

  @computed('page', 'totalPages')
  get disableNextPage() {
    return this.page >= this.totalPages;
  }

  @computed('page', 'total')
  get pageControlsDisplayString() {
    const { total, page } = this;

    const displayPageMin = (page - 1) * PAGE_SIZE + 1;
    const displayPageMax = Math.min(page * PAGE_SIZE, total);

    return `${displayPageMin} - ${displayPageMax} of ${total}`;
  }

  @action
  setType(type) {
    this.setProperties({
      type,
      page: 1,
    });
  }

  @action
  setPage(page, increment) {
    this.set('page', page + increment);
  }

  @action
  toggleEditor(value) {
    this.setProperties({
      errors: {},
      isShowingEditor: value,
    });
  }

  @action
  onSaveClicked(type, title, content) {
    const modalModel = {
      titleText: 'Are you sure you want to save this document?',
      bodyText: `Are you sure you want to save "${title}"?`,
      actionButtonText: 'Save',
      cancelButtonText: 'Cancel',
      width: 'medium',
      confirmAction: () => {
        this.saveLegal.perform(type, title, content);
      },
    };

    showModal(this, ConfirmationModal, modalModel);
  }

  @restartableTask
  *loadLegals(type, page) {
    try {
      const response = yield this.legalCommandService.loadLegals([type], page, PAGE_SIZE);
      this.shData.pushResponseToStore(response);
      const legals = this.shData.getModelArrayFromResponse(response, 'legal');
      this.set('legals', legals);
      return response;
    } catch (error) {
      handleErrorResponseWithModal(this, error);
    }
  }

  @restartableTask
  *saveLegal(type, title, content) {
    this.set('errors', {});
    try {
      yield this.legalCommandService.createLegal(type, title, content);
      this.setProperties({
        errors: {},
        isShowingEditor: false,
        page: 1,
      });
      yield this.loadLegals.perform(type, 1);
    } catch (error) {
      handleFormErrorOrModal(
        this,
        error,
        HTML_PREVIEW_FORM_INPUTS,
        false,
        'There was a problem saving document'
      );
    }
  }
}

export default AdminLegalController;
